import React, { useEffect, useMemo, useRef } from 'react'
import ReactQuill from 'react-quill-new'
import 'react-quill-new/dist/quill.snow.css'

interface RichTextEditorProps {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  className?: string
  height?: string
  enableTabNavigation?: boolean
}

const findFocusableElements = (root: HTMLElement) => {
  const selector = [
    'button:not([disabled])',
    'input:not([disabled])',
    'textarea:not([disabled])',
    'select:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
    '[contenteditable="true"]',
    '.ql-editor'
  ].join(', ')

  return Array.from(root.querySelectorAll<HTMLElement>(selector)).filter((element) => {
    if (element.getAttribute('aria-hidden') === 'true') return false
    if (element.closest('.ql-toolbar')) return false
    const isEditorSurface =
      element.classList.contains('ql-editor') ||
      element.getAttribute('contenteditable') === 'true'
    if (!isEditorSurface && element.tabIndex < 0) return false
    return !!(element.offsetWidth || element.offsetHeight || element.getClientRects().length)
  })
}

const findToolbarFocusableElements = (root: HTMLElement) => {
  const toolbar = root.closest<HTMLElement>('.rich-text-editor')?.querySelector<HTMLElement>('.ql-toolbar')
  if (!toolbar) return []

  return Array.from(toolbar.querySelectorAll<HTMLElement>([
    'button',
    'select',
    'input',
    'textarea',
    '[role="button"]',
    '[tabindex]'
  ].join(', ')))
}

const moveFocusFrom = (current: HTMLElement, direction: 'next' | 'prev') => {
  const scope =
    current.closest<HTMLElement>('[data-focus-scope="survey-form"]') ||
    current.closest<HTMLElement>('form') ||
    current.closest<HTMLElement>('[role="dialog"]') ||
    document.body

  const focusable = findFocusableElements(scope)
  const currentIndex = focusable.findIndex((element) => (
    element === current ||
    element.contains(current) ||
    current.contains(element)
  ))
  if (currentIndex === -1) return

  const nextIndex = direction === 'next' ? currentIndex + 1 : currentIndex - 1
  const target = focusable[nextIndex]
  target?.focus()
}

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  placeholder = 'Digite o texto...',
  className = '',
  height = '120px',
  enableTabNavigation = true
}) => {
  const quillRef = useRef<ReactQuill>(null)

  const modules = useMemo(() => ({
    toolbar: {
      container: [
        ['bold', 'italic', 'underline', 'strike'],
        [{ color: [] }, { background: [] }],
        [{ align: [] }],
        [{ list: 'ordered' }, { list: 'bullet' }],
        [{ size: ['small', false, 'large', 'huge'] }],
        [{ header: [1, 2, 3, 4, 5, 6, false] }],
        ['link'],
        ['clean']
      ]
    },
    clipboard: {
      matchVisual: false
    }
  }), [])

  const formats = [
    'header', 'font', 'size',
    'bold', 'italic', 'underline', 'strike', 'blockquote',
    'list',
    'link', 'color', 'background',
    'align'
  ]

  useEffect(() => {
    if (!enableTabNavigation) return

    const editor = quillRef.current?.getEditor()
    const root = editor?.root
    if (!root) return

    const toolbarElements = findToolbarFocusableElements(root)
    const previousToolbarTabIndices = new Map<HTMLElement, string | null>()
    toolbarElements.forEach((element) => {
      previousToolbarTabIndices.set(element, element.getAttribute('tabindex'))
      element.tabIndex = -1
    })

    const previousEditorTabIndex = root.getAttribute('tabindex')
    root.tabIndex = 0

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Tab') return

      event.preventDefault()
      event.stopPropagation()
      event.stopImmediatePropagation()
      moveFocusFrom(root, event.shiftKey ? 'prev' : 'next')
    }

    root.addEventListener('keydown', handleKeyDown, true)
    return () => {
      root.removeEventListener('keydown', handleKeyDown, true)

      if (previousEditorTabIndex === null) {
        root.removeAttribute('tabindex')
      } else {
        root.setAttribute('tabindex', previousEditorTabIndex)
      }

      previousToolbarTabIndices.forEach((tabIndex, element) => {
        if (tabIndex === null) {
          element.removeAttribute('tabindex')
        } else {
          element.setAttribute('tabindex', tabIndex)
        }
      })
    }
  }, [enableTabNavigation])

  return (
    <div className={`rich-text-editor min-w-0 ${className}`}>
      <style>{`
        .rich-text-editor {
          min-width: 0;
        }

        .rich-text-editor .ql-editor {
          min-height: ${height};
          font-family: inherit;
          overflow-wrap: break-word;
          word-break: normal;
          hyphens: none;
        }

        .rich-text-editor .ql-toolbar {
          border-top: 1px solid #e5e7eb;
          border-left: 1px solid #e5e7eb;
          border-right: 1px solid #e5e7eb;
          border-radius: 0.5rem 0.5rem 0 0;
          background: #f9fafb;
        }

        .rich-text-editor .ql-container {
          border-bottom: 1px solid #e5e7eb;
          border-left: 1px solid #e5e7eb;
          border-right: 1px solid #e5e7eb;
          border-radius: 0 0 0.5rem 0.5rem;
          font-family: inherit;
          min-width: 0;
        }

        .rich-text-editor .ql-editor p,
        .rich-text-editor .ql-editor li {
          overflow-wrap: break-word;
          word-break: normal;
          hyphens: none;
        }

        .rich-text-editor .ql-editor.ql-blank::before {
          color: #9ca3af;
          font-style: normal;
        }

        .rich-text-editor .ql-toolbar .ql-formats {
          margin-right: 15px;
        }

        .rich-text-editor .ql-toolbar button {
          padding: 5px;
          margin: 0 1px;
        }

        .rich-text-editor .ql-toolbar button:hover,
        .rich-text-editor .ql-toolbar button.ql-active,
        .rich-text-editor .ql-toolbar .ql-picker-label:hover {
          color: #3b82f6;
        }

        .rich-text-editor .ql-toolbar .ql-picker-label {
          border: none;
          padding: 2px 5px;
        }

        @media (max-width: 768px) {
          .rich-text-editor .ql-toolbar {
            padding: 8px;
          }

          .rich-text-editor .ql-toolbar .ql-formats {
            margin-right: 8px;
            margin-bottom: 5px;
          }
        }
      `}</style>

      <ReactQuill
        ref={quillRef}
        theme="snow"
        value={value}
        onChange={onChange}
        modules={modules}
        formats={formats}
        placeholder={placeholder}
      />
    </div>
  )
}

export default RichTextEditor
